// src/constants/api.ts
var API_ENDPOINTS = {
  // Call tracing — per-system call timeline (see types/telemetry.ts)
  // Device single-event report (DeviceToken auth). Lives under /device/* rather
  // than /calls/* because the API's Asterisk router gates ALL /calls/* paths
  // behind the shared secret. Body carries the CallTraceEvent, so the messy
  // correlation id (SIP Call-ID / linkedid) never has to be URL-encoded.
  // (Asterisk reports its own steps to /api/v1/calls/event with the secret.)
  CALL_EVENT_REPORT: "/api/v1/device/call-event"};

// src/telemetry/CallTracer.ts
var CallTracer = class {
  sink;
  defaultSource;
  constructor(sink, options = {}) {
    this.sink = sink;
    this.defaultSource = options.defaultSource ?? "device";
  }
  /**
   * Report a step. Returns immediately; the POST runs in the background.
   *
   * @param callId correlation key — the SIP Call-ID the device observes, or the
   *   X-Caro-Call-Id (Asterisk linkedid) header value when present.
   */
  emit(callId, step, opts = {}) {
    if (!callId) {
      console.warn("[CallTracer] Dropping event with no callId:", step);
      return;
    }
    const event = {
      callId,
      step,
      source: opts.source ?? this.defaultSource,
      level: opts.level ?? "info",
      at: (/* @__PURE__ */ new Date()).toISOString(),
      ...opts.detail ? { detail: opts.detail } : {}
    };
    console.log(`[CallTracer] ${event.source}:${step}`, callId, opts.detail ?? "");
    void this.sink.post(API_ENDPOINTS.CALL_EVENT_REPORT, event).catch((err) => {
      console.warn(`[CallTracer] Failed to report ${step}:`, err?.message ?? err);
    });
  }
};

// src/telemetry/ConnectivityMonitor.ts
var LAYER_KEYS = ["network", "internet", "api", "sip"];
var ConnectivityMonitor = class {
  deps;
  now;
  checkIntervalMs;
  keepaliveMs;
  maxBuffer;
  timer = null;
  buffer = [];
  lastFlushAt = null;
  lastFlushedLayers = null;
  running = false;
  loaded = false;
  checking = false;
  constructor(deps, options = {}) {
    this.deps = deps;
    this.now = deps.now ?? (() => Date.now());
    this.checkIntervalMs = options.checkIntervalMs ?? 6e4;
    this.keepaliveMs = options.keepaliveMs ?? 9e5;
    this.maxBuffer = options.maxBuffer ?? 240;
  }
  /** Begin sampling. Rehydrates any buffered samples from a prior run first. */
  async start() {
    if (this.running) return;
    this.running = true;
    await this.loadState();
    await this.check();
    this.timer = setInterval(() => {
      void this.check();
    }, this.checkIntervalMs);
  }
  stop() {
    this.running = false;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
  /** Take one sample, buffer it, and flush if state changed or keepalive due. */
  async check() {
    if (this.checking) return;
    this.checking = true;
    try {
      const reading = await this.deps.probe();
      const sample = { ...reading, at: new Date(this.now()).toISOString() };
      this.buffer.push(sample);
      if (this.buffer.length > this.maxBuffer) {
        const dropped = this.buffer.length - this.maxBuffer;
        this.buffer.splice(0, dropped);
        console.warn(`[Connectivity] Buffer cap reached \u2014 dropped ${dropped} oldest sample(s)`);
      }
      await this.persist();
      const changed = this.didChange(sample);
      const keepaliveDue = this.lastFlushAt === null || this.now() - this.lastFlushAt >= this.keepaliveMs;
      if (changed || keepaliveDue) {
        await this.doFlush(changed ? "change" : "keepalive", sample);
      }
    } catch (err) {
      console.warn("[Connectivity] check failed:", err?.message ?? err);
    } finally {
      this.checking = false;
    }
  }
  /** True when any monitored layer flipped vs. the last successfully-flushed state. */
  didChange(sample) {
    if (!this.lastFlushedLayers) return true;
    return LAYER_KEYS.some((k) => this.lastFlushedLayers[k] !== sample[k]);
  }
  async doFlush(reason, current) {
    const heartbeat = {
      reason,
      current,
      samples: [...this.buffer]
    };
    try {
      await this.deps.flush(heartbeat);
      this.buffer = [];
      this.lastFlushAt = this.now();
      this.lastFlushedLayers = {
        network: current.network,
        internet: current.internet,
        api: current.api,
        sip: current.sip
      };
      await this.persist();
    } catch (err) {
      console.warn(`[Connectivity] flush (${reason}) failed \u2014 retaining ${this.buffer.length} sample(s):`, err?.message ?? err);
    }
  }
  async loadState() {
    if (this.loaded) return;
    this.loaded = true;
    try {
      const raw = await this.deps.storage.read();
      if (!raw) return;
      const state = JSON.parse(raw);
      if (Array.isArray(state.samples)) this.buffer = state.samples.slice(-this.maxBuffer);
      this.lastFlushAt = state.lastFlushAt ? new Date(state.lastFlushAt).getTime() : null;
      this.lastFlushedLayers = state.lastFlushedLayers ?? null;
    } catch (err) {
      console.warn("[Connectivity] Failed to load persisted buffer:", err?.message ?? err);
    }
  }
  async persist() {
    const state = {
      samples: this.buffer,
      lastFlushAt: this.lastFlushAt ? new Date(this.lastFlushAt).toISOString() : null,
      lastFlushedLayers: this.lastFlushedLayers
    };
    try {
      await this.deps.storage.write(JSON.stringify(state));
    } catch (err) {
      console.warn("[Connectivity] Failed to persist buffer:", err?.message ?? err);
    }
  }
};

export { CallTracer, ConnectivityMonitor };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map